import React, { useState, useCallback } from 'react';
import { stripQuillColors } from '../../../lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MessageSquare, ThumbsUp, Clock,
  ChevronDown, ChevronUp, Edit, Trash2,
  AlertTriangle, Send, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Skeleton } from '../../../components/Skeleton';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  ForumPost, ForumComentario, ForumLike,
  ForumCategoriaLabels, ForumCategoriaColors,
} from '../../../types/forum';
import { ForumComentarioItem } from './ForumComentarioItem';
import { ForumPostForm } from './ForumPostForm';
import { cn } from '../../../lib/utils';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useToast } from '../../../hooks/use-toast';
import {
  getForumComentarios,
  createForumComentario,
  replyForumComentario,
  updateForumComentario,
  deleteForumComentario,
  moderateForumComentario,
  toggleComentarioLike,
  togglePostLike,
  getPostLikes,
  updateForumPost,
  moderateForumPost,
  deleteForumPost,
} from '../../../services/forumService';

interface ForumPostCardProps {
  post: ForumPost;
  onPostUpdated?: (updatedPost: ForumPost) => void;
  onPostDeleted?: (postId: string) => void;
}

export function ForumPostCard({
  post: initialPost,
  onPostUpdated,
  onPostDeleted,
}: ForumPostCardProps) {
  const { militar } = useAuth();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  const [post, setPost] = useState<ForumPost>(initialPost);
  const [comentarios, setComentarios] = useState<ForumComentario[]>([]);
  const [showComentarios, setShowComentarios] = useState(false);
  const [likers, setLikers] = useState<ForumLike[]>([]);
  const [loadingComentarios, setLoadingComentarios] = useState(false);
  const [isLikingPost, setIsLikingPost] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canModerate = hasPermission('forum.moderar');
  const canDeleteAny = hasPermission('forum.deletar_qualquer');
  const isOwner = militar?.id === post.militar_id;
  const canEdit = isOwner && post.ativo;
  const canDelete = canDeleteAny || (hasPermission('forum.deletar_post_proprio') && isOwner);
  const canComment = hasPermission('forum.comentar');

  const timeAgo = formatDistanceToNow(new Date(post.criado_em), {
    addSuffix: true,
    locale: ptBR,
  });

  const habboAvatar = (nick: string, size = 'b') =>
    `https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(nick)}&action=std&direction=4&head_direction=2&img_format=png&gesture=std&headonly=1&size=${size}`;

  const loadComentarios = useCallback(async () => {
    setLoadingComentarios(true);
    try {
      const [data, postLikers] = await Promise.all([
        getForumComentarios(post.id),
        getPostLikes(post.id),
      ]);
      setComentarios(data);
      setLikers(postLikers);
    } catch {
      // silently fail
    } finally {
      setLoadingComentarios(false);
    }
  }, [post.id]);

  // --- Handlers de comentário ---
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setIsAddingComment(true);
    try {
      await createForumComentario(post.id, { conteudo: newComment });
      setNewComment('');
      await loadComentarios();
      setPost((prev) => ({ ...prev, comentarios_count: (prev.comentarios_count || 0) + 1 }));
      toast({ title: 'Sucesso', description: 'Comentário adicionado!' });
    } catch (err: any) {
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao comentar', variant: 'destructive' });
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleReplyComment = async (comentarioId: string, conteudo: string) => {
    await replyForumComentario(comentarioId, { conteudo });
    await loadComentarios();
    toast({ title: 'Sucesso', description: 'Resposta adicionada!' });
  };

  const handleEditComment = async (comentarioId: string, conteudo: string) => {
    await updateForumComentario(comentarioId, conteudo);
    await loadComentarios();
    toast({ title: 'Sucesso', description: 'Comentário atualizado!' });
  };

  const handleDeleteComment = async (comentarioId: string) => {
    await deleteForumComentario(comentarioId);
    await loadComentarios();
    setPost((prev) => ({ ...prev, comentarios_count: Math.max(0, (prev.comentarios_count || 0) - 1) }));
    toast({ title: 'Sucesso', description: 'Comentário excluído!' });
  };

  const handleModerateComment = async (comentarioId: string, ativo: boolean) => {
    await moderateForumComentario(comentarioId, ativo);
    await loadComentarios();
    toast({ title: 'Sucesso', description: `Comentário ${ativo ? 'ativado' : 'inativado'}!` });
  };

  const updateComentariosLike = (
    list: ForumComentario[],
    id: string,
    result: { liked: boolean; likes_count: number }
  ): ForumComentario[] =>
    list.map((c) => {
      if (c.id === id) return { ...c, user_liked: result.liked, likes_count: result.likes_count };
      if (c.respostas) return { ...c, respostas: updateComentariosLike(c.respostas, id, result) };
      return c;
    });

  const toggleComentariosLikeOptimistic = (
    list: ForumComentario[],
    id: string,
  ): ForumComentario[] =>
    list.map((c) => {
      if (c.id === id) return { ...c, user_liked: !c.user_liked, likes_count: (c.likes_count ?? 0) + (c.user_liked ? -1 : 1) };
      if (c.respostas) return { ...c, respostas: toggleComentariosLikeOptimistic(c.respostas, id) };
      return c;
    });

  const handleLikeComment = async (comentarioId: string) => {
    // Atualização otimista imediata
    setComentarios((prev) => toggleComentariosLikeOptimistic(prev, comentarioId));
    try {
      const result = await toggleComentarioLike(comentarioId);
      // Aplica o valor real retornado pelo servidor
      setComentarios((prev) => updateComentariosLike(prev, comentarioId, result));
    } catch (err: any) {
      // Reverte em caso de erro
      setComentarios((prev) => toggleComentariosLikeOptimistic(prev, comentarioId));
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao curtir', variant: 'destructive' });
    }
  };

  // --- Like do post ---
  const handleLikePost = async () => {
    const newLiked = !post.user_liked;
    const newCount = (post.likes_count ?? 0) + (newLiked ? 1 : -1);
    setPost((prev) => ({ ...prev, user_liked: newLiked, likes_count: newCount }));
    setIsLikingPost(true);
    try {
      const result = await togglePostLike(post.id);
      setPost((prev) => ({ ...prev, user_liked: result.liked, likes_count: result.likes_count }));
      onPostUpdated?.({ ...post, user_liked: result.liked, likes_count: result.likes_count });
      // Atualiza lista de quem curtiu
      const updatedLikers = await getPostLikes(post.id);
      setLikers(updatedLikers);
    } catch (err: any) {
      // Reverte
      setPost((prev) => ({ ...prev, user_liked: !newLiked, likes_count: (prev.likes_count ?? 0) + (newLiked ? -1 : 1) }));
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao curtir', variant: 'destructive' });
    } finally {
      setIsLikingPost(false);
    }
  };

  // --- Handlers do post ---
  const handleUpdatePost = async (data: any) => {
    setIsSubmitting(true);
    try {
      const updated = await updateForumPost(post.id, data);
      setPost(updated);
      setShowEditDialog(false);
      onPostUpdated?.(updated);
      toast({ title: 'Sucesso', description: 'Post atualizado!' });
    } catch (err: any) {
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao atualizar', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeratePost = async () => {
    setIsSubmitting(true);
    try {
      const updated = await moderateForumPost(post.id, !post.ativo);
      setPost(updated);
      onPostUpdated?.(updated);
      toast({ title: 'Sucesso', description: `Post ${updated.ativo ? 'ativado' : 'inativado'}!` });
    } catch (err: any) {
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao moderar', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    setIsSubmitting(true);
    try {
      await deleteForumPost(post.id);
      onPostDeleted?.(post.id);
      toast({ title: 'Sucesso', description: 'Post excluído!' });
    } catch (err: any) {
      toast({ title: 'Erro', description: err.response?.data?.message || 'Falha ao excluir', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  const topLevelComentarios = comentarios.filter((c) => !c.comentario_pai_id);

  return (
    <>
      <Card className={cn('transition-shadow', !post.ativo && 'opacity-60', 'shadow-sm')}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={cn(ForumCategoriaColors[post.categoria], 'text-white text-xs')}>
                  {ForumCategoriaLabels[post.categoria]}
                </Badge>
                {!post.ativo && <Badge variant="destructive" className="text-xs">Inativo</Badge>}
              </div>
              {post.militar && (
                <div className="flex items-center gap-3">
                  <img
                    src={habboAvatar(post.militar.nick, 'l')}
                    alt={post.militar.nick}
                    className="w-14 h-14 object-contain shrink-0"
                  />
                  <div className="flex flex-col">
                    {post.militar.patente && (
                      <span className="text-sm text-muted-foreground font-medium leading-tight">
                        {post.militar.patente.patente}
                      </span>
                    )}
                    <a
                      href={`https://www.exercitobrhb.net/perfil/${post.militar.nick}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-bold text-foreground leading-tight hover:underline hover:text-primary transition-colors"
                    >
                      {post.militar.nick}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {(canEdit || canModerate || canDelete) && (
              <div className="flex gap-1 shrink-0">
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    title="Editar"
                    onClick={() => setShowEditDialog(true)}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canModerate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    title={post.ativo ? 'Inativar' : 'Ativar'}
                    onClick={handleModeratePost}
                    disabled={isSubmitting}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    title="Excluir"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="-mt-6 pb-4 space-y-6">
          {/* Conteúdo completo do post */}
          <div
            className="prose prose-sm dark:prose-invert max-w-none forum-content"
            dangerouslySetInnerHTML={{ __html: stripQuillColors(post.conteudo) }}
          />

          {/* Seção de comentários */}
          <div className="border-t pt-4">
              <div
                role="button"
                tabIndex={0}
                className="flex items-center justify-between w-full mb-3 group cursor-pointer"
                onClick={() => {
                  if (!showComentarios) loadComentarios();
                  setShowComentarios((prev) => !prev);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!showComentarios) loadComentarios();
                    setShowComentarios((prev) => !prev);
                  }
                }}
              >
                <h4 className="font-medium text-sm flex items-center gap-2 group-hover:text-primary transition-colors">
                  <MessageSquare className="h-4 w-4" />
                  Comentários ({post.comentarios_count ?? topLevelComentarios.length ?? 0})
                </h4>
                <div className="flex items-center gap-1">
                  {showComentarios && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={(e) => { e.stopPropagation(); loadComentarios(); }}
                      disabled={loadingComentarios}
                      title="Atualizar comentários"
                    >
                      <RefreshCw className={cn('h-3.5 w-3.5', loadingComentarios && 'animate-spin')} />
                    </Button>
                  )}
                  {showComentarios
                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  }
                </div>
              </div>

              {showComentarios && (
                <>
              {/* Formulário de novo comentário */}
              {post.ativo && canComment && (
                <div className="mb-4 flex gap-2 items-end">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Escreva um comentário... (Ctrl+Enter para enviar)"
                    rows={2}
                    className="flex-1 resize-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) handleAddComment();
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={isAddingComment || !newComment.trim()}
                    className="shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Lista de comentários */}
              {loadingComentarios ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="border-l-2 border-primary/30 pl-4">
                      <Skeleton className="h-3 w-32 mb-2" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  ))}
                </div>
              ) : topLevelComentarios.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nenhum comentário ainda. Seja o primeiro!
                </p>
              ) : (
                <div className="space-y-3">
                  {topLevelComentarios.map((c) => (
                    <ForumComentarioItem
                      key={c.id}
                      comentario={c}
                      onLike={handleLikeComment}
                      onReply={handleReplyComment}
                      onEdit={handleEditComment}
                      onDelete={handleDeleteComment}
                      onModerate={handleModerateComment}
                      canModerate={canModerate}
                      canDelete={canDeleteAny || hasPermission('forum.deletar_comentario_proprio')}
                    />
                  ))}
                </div>
              )}
                </>
              )}
            </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-2 pt-2 border-t text-sm text-muted-foreground">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                {post.militar?.nick ? (
                  <img
                    src={habboAvatar(post.militar.nick, 's')}
                    alt={post.militar.nick}
                    title={post.militar.nick}
                    className="w-7 h-7"
                  />
                ) : null}
                <span className="text-xs">{post.militar?.patente?.patente} {post.militar?.nick || 'Anônimo'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span className="text-xs">{timeAgo}</span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 px-2 text-xs">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>{post.comentarios_count ?? post.comentarios?.length ?? 0}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className={cn('flex items-center gap-1 px-2 h-7 text-xs', post.user_liked && 'text-primary')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLikePost();
                }}
                disabled={isLikingPost}
              >
                <ThumbsUp className={cn('h-3.5 w-3.5', post.user_liked && 'fill-current')} />
                <span>{post.likes_count ?? post.likes?.length ?? 0}</span>
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Dialog de edição */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Post</DialogTitle>
          </DialogHeader>
          <ForumPostForm
            initialData={post}
            onSubmit={handleUpdatePost}
            onCancel={() => setShowEditDialog(false)}
            isLoading={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir post?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            Esta ação não pode ser desfeita. O post e todos os seus comentários serão permanentemente excluídos.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeletePost} disabled={isSubmitting}>Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
